import React, { useState, useRef, useEffect } from "react";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";

import { styled, emphasize } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import classnames from "classnames";

import "aos/dist/aos.css";
import AOS from "aos";
import Typed from "react-typed";

const maxWidth = 1000;

const createPageClasses = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.paper,
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
    },
}));

export const PublicPage = ({ children }) => {
    const classes = createPageClasses();
    useEffect(() => {
        AOS.init({
            once: true,
            delay: 250,
        });
        AOS.refresh();
    }, []);

    return <div className={classes.root}>{children}</div>;
};

export const PublicPageContent = ({ children }) => {
    return <Box sx={{ flex: "1 1 auto" }}>{children}</Box>;
};

const createHeaderClasses = makeStyles((theme) => ({
    root: {
        transition: theme.transitions.create("all", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    hidden: {
        transform: `translateY(-100%)`,
    },
}));

const Offset = styled("div")(({ theme }) => ({ ...theme.mixins.toolbar, flex: "0 0 auto" }));

export const PublicHeader = ({ children }) => {
    const classes = createHeaderClasses();
    const prevScrollY = useRef(0);

    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (prevScrollY.current < currentScrollY && currentScrollY > 64) {
                setVisible(false);
            }
            if (prevScrollY.current > currentScrollY) {
                setVisible(true);
            }

            prevScrollY.current = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <>
            <AppBar position="fixed" className={classnames({ [classes.hidden]: !visible }, classes.root)}>
                <Container sx={{ maxWidth }}>
                    <Toolbar>
                        <div style={{ width: "100%" }}>{children}</div>
                    </Toolbar>
                </Container>
            </AppBar>
            <Offset />
        </>
    );
};

const createTextClasses = makeStyles((theme) => ({
    title: {
        fontSize: "3.15rem",
        fontWeight: 100,
        lineHeight: 1.2,
    },
    subtitle: {
        fontSize: "1rem",
        lineHeight: 1.5,
        textTransform: "uppercase",
        fontWeight: 500,
        textAlign: "center",
    },
    left: {
        textAlign: "left",
    },
    right: {
        textAlign: "right",
    },
}));

export const Title = ({ children, gutterBottom }) => {
    const classes = createTextClasses();
    return (
        <Typography variant="h1" className={classnames(classes.title, "PublicTitle")} gutterBottom={gutterBottom}>
            {children}
        </Typography>
    );
};

export const Subtitle = ({ children, left, right }) => {
    const classes = createTextClasses();
    return (
        <Typography
            align="center"
            variant="body1"
            className={classnames(classes.subtitle, { [classes.left]: !!left, [classes.right]: !!right }, "PublicSubtitle")}
            component="div"
        >
            {children}
        </Typography>
    );
};

export const Text = ({ children }) => {
    return (
        <Typography sx={{ color: "textSecondary", fontSize: "1.05rem", fontWeight: 400, py: 4 }} gutterBottom>
            {children}
        </Typography>
    );
};

export const TypedText = ({ color, ...props }) => {
    return (
        <font color={color}>
            <Typed typeSpeed={75} loop={true} {...props} />
        </font>
    );
};

const createSectionClasses = makeStyles((theme) => ({
    root: {
        display: "flex",
        overflow: "hidden",
        [theme.breakpoints.up("sm")]: {
            flexWrap: "wrap",
        },
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap-reverse",
            "& .PublicSubtitle": {
                textAlign: "center",
            },
        },
        rowGap: theme.spacing(6),
        columnGap: theme.spacing(12),
        "& > *": {
            //border: "1px solid red",
            flex: "1 1",
            [theme.breakpoints.down("md")]: {
                textAlign: "center",
                minWidth: "100%",
            },
        },
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        "& .PublicSubtitle": {
            color: theme.palette.text.secondary,
        },
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
    },
    grey: {
        backgroundColor: theme.palette.grey.main,
        color: emphasize(theme.palette.getContrastText(theme.palette.grey.main), 0.2),
        "& .PublicSubtitle": {
            color: emphasize(theme.palette.getContrastText(theme.palette.grey.main), 0.4),
        },
    },
    primary: (props) => ({
        backgroundColor: theme.palette.primary[props.variant || "main"],
        color: theme.palette.getContrastText(theme.palette.primary[props.variant || "main"]),
        "& .PublicSubtitle": {
            color: emphasize(theme.palette.getContrastText(theme.palette.primary[props.variant || "main"]), 0.2),
        },
    }),
    secondary: (props) => ({
        backgroundColor: theme.palette.secondary[props.variant || "main"],
        color: theme.palette.getContrastText(theme.palette.secondary[props.variant || "main"]),
        "& .PublicSubtitle": {
            color: emphasize(theme.palette.getContrastText(theme.palette.secondary[props.variant || "main"]), 0.2),
        },
    }),
}));

export const Section = ({ children, color, variant, style, width, refProp }) => {
    const classes = createSectionClasses({ variant });
    return (
        <div
            className={classnames(classes.paper, {
                [classes.grey]: color === "grey",
                [classes.primary]: color === "primary",
                [classes.secondary]: color === "secondary",
            })}
            style={style}
            ref={refProp}
        >
            <Container maxWidth={width || "lg"} sx={{ maxWidth, zIndex: 1 }}>
                <div className={classes.root}>{children}</div>
            </Container>
        </div>
    );
};

const createImageClasses = makeStyles((theme) => ({
    imageRoot: (props) => ({
        display: "flex",
        justifyContent: "center",
        //backgroundImage: `url(${props.src})`,
        backgroundImage: `url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8' standalone='no'%3F%3E%3C!-- Generator: Adobe Illustrator 24.2.1, SVG Export Plug-In . SVG Version: 6.00 Build 0) --%3E%3Csvg version='1.1' id='Layer_1' x='0px' y='0px' viewBox='0 0 31.055599 19.31439' xml:space='preserve' sodipodi:docname='cloud.svg' width='31.055599' height='19.31439' inkscape:version='1.2.2 (732a01da63, 2022-12-09)' xmlns:inkscape='http://www.inkscape.org/namespaces/inkscape' xmlns:sodipodi='http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns='http://www.w3.org/2000/svg' xmlns:svg='http://www.w3.org/2000/svg'%3E%3Cdefs id='defs9'%3E%3ClinearGradient inkscape:collect='always' id='linearGradient3108'%3E%3Cstop style='stop-color:%23aa15ba;stop-opacity:1;' offset='0' id='stop3104' /%3E%3Cstop style='stop-color:%23aa15ba;stop-opacity:0.67777777;' offset='0.56886226' id='stop3106' /%3E%3C/linearGradient%3E%3ClinearGradient id='linearGradient3084' inkscape:swatch='solid'%3E%3Cstop style='stop-color:%23d1af6e;stop-opacity:1;' offset='0' id='stop3082' /%3E%3C/linearGradient%3E%3ClinearGradient inkscape:collect='always' id='linearGradient1094'%3E%3Cstop style='stop-color:%2333a36e;stop-opacity:0.40000001;' offset='0' id='stop1090' /%3E%3Cstop style='stop-color:%2333a36e;stop-opacity:1;' offset='0.61976045' id='stop1092' /%3E%3C/linearGradient%3E%3ClinearGradient inkscape:collect='always' id='linearGradient1078'%3E%3Cstop style='stop-color:%23cb0e0e;stop-opacity:1;' offset='0.58982038' id='stop1074' /%3E%3Cstop style='stop-color:%23cb0e0b;stop-opacity:0.38333333;' offset='1' id='stop1076' /%3E%3C/linearGradient%3E%3ClinearGradient inkscape:collect='always' id='linearGradient1070'%3E%3Cstop style='stop-color:%23cf2b6e;stop-opacity:1;' offset='0.19161677' id='stop1066' /%3E%3Cstop style='stop-color:%234e0000;stop-opacity:1;' offset='0.93113774' id='stop1068' /%3E%3C/linearGradient%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath974'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path976' style='fill:%23353535' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath978'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path980' style='fill:%23353535' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath982'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path984' style='fill:%23353535' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath986'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path988' style='fill:%23353535' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath986-1'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path988-5' style='fill:%23353535' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath296'%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path298' clip-path='url(%23clipPath986-1)' style='fill:%234433aa;fill-opacity:1' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath300'%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path302' clip-path='url(%23clipPath986-1)' style='fill:%234433aa;fill-opacity:1' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath304'%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path306' clip-path='url(%23clipPath986-1)' style='fill:%234433aa;fill-opacity:1' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath308'%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path310' clip-path='url(%23clipPath986-1)' style='fill:%234433aa;fill-opacity:1' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath312'%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path314' clip-path='url(%23clipPath986-1)' style='fill:%234433aa;fill-opacity:1' /%3E%3C/clipPath%3E%3CclipPath clipPathUnits='userSpaceOnUse' id='clipPath986-2'%3E%3Cpath class='st0' d='m 31.051196,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.006,0 -0.012,6.5e-4 -0.0171,6.5e-4 0.0107,-0.1598698 0.0171,-0.3203298 0.0171,-0.4827198 C 26.105266,3.41502 22.689606,0 18.477816,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.3225402 4.60729,7.9263002 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.082156 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.004 z' id='path988-6' style='fill:%23353535' /%3E%3C/clipPath%3E%3ClinearGradient inkscape:collect='always' xlink:href='%23linearGradient1070' id='linearGradient1072' x1='0' y1='11.670189' x2='21.76763' y2='11.670189' gradientUnits='userSpaceOnUse' /%3E%3ClinearGradient inkscape:collect='always' xlink:href='%23linearGradient1078' id='linearGradient1080' x1='0' y1='9.657195' x2='21.76763' y2='9.657195' gradientUnits='userSpaceOnUse' /%3E%3ClinearGradient inkscape:collect='always' xlink:href='%23linearGradient1094' id='linearGradient1096' x1='4.98643' y1='13.580827' x2='31.055599' y2='13.580827' gradientUnits='userSpaceOnUse' /%3E%3ClinearGradient inkscape:collect='always' xlink:href='%23linearGradient3108' id='linearGradient3110' x1='10.986171' y1='9.657195' x2='27.704256' y2='9.657195' gradientUnits='userSpaceOnUse' /%3E%3C/defs%3E%3Csodipodi:namedview id='namedview7' pagecolor='%23ffffff' bordercolor='%23000000' borderopacity='0.25' inkscape:showpageshadow='2' inkscape:pageopacity='0.0' inkscape:pagecheckerboard='0' inkscape:deskcolor='%23d1d1d1' showgrid='false' inkscape:zoom='29.309576' inkscape:cx='8.120213' inkscape:cy='12.487386' inkscape:window-width='1920' inkscape:window-height='1009' inkscape:window-x='1912' inkscape:window-y='-8' inkscape:window-maximized='1' inkscape:current-layer='Layer_1' /%3E%3Cstyle type='text/css' id='style2'%3E .st0%7Bfill:%23353535;%7D .st1%7Benable-background:new ;%7D .st2%7Bfill:%23373737;%7D .st3%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23373737;%7D .st4%7Bfill:%231A1A1A;%7D .st5%7Bfill-rule:evenodd;clip-rule:evenodd;fill:%23353535;%7D .st6%7Bfill:%23333333;%7D%0A%3C/style%3E%3Cpath style='opacity:0.81099656;fill:url(%23linearGradient1072);stroke-width:1.31528;fill-opacity:1' d='M 21.76763,19.31439 H -4.844833 L 0,4.025988 4.60729,7.9263002 Z' id='path268' clip-path='url(%23clipPath308)' /%3E%3Cpath style='opacity:0.69415808;fill:url(%23linearGradient1080);stroke-width:1.31528;fill-opacity:1' d='M 21.76763,19.31439 4.60729,7.9263002 0,4.025988 10.986171,0 11.92832,3.7158 Z' id='path270' clip-path='url(%23clipPath304)' /%3E%3Cpath style='opacity:0.87285223;fill:url(%23linearGradient3110);stroke-width:1.31528;fill-opacity:1' d='M 21.76763,19.31439 11.92832,3.7158 10.986171,0 h 16.718085 l -1.61609,8.11017 z' id='path272' clip-path='url(%23clipPath300)' /%3E%3Cpath style='opacity:1;fill:url(%23linearGradient1096);stroke-width:1.315;fill-opacity:1;fill-rule:nonzero;stroke:none;stroke-opacity:1;stroke-dasharray:none' d='M 26.10527,8.10952 4.98643,19.31439 H 31.055599 V 7.8472647 Z' id='path274' clip-path='url(%23clipPath296)' /%3E%3Cpath class='st0' d='m 31.0512,12.90256 c -0.0834,-2.66062 -2.26512,-4.79304 -4.94593,-4.79304 -0.0057,0 -0.01204,6.5e-4 -0.01708,6.5e-4 C 26.09893,7.9503 26.10527,7.78984 26.10527,7.62745 26.10527,3.41502 22.68961,0 18.47782,0 15.69466,0 13.2608,1.49048 11.92832,3.7158 11.30404,3.45927 10.61913,3.31648 9.90078,3.31648 7.19907,3.31648 4.96617,5.32254 4.60729,7.9263 2.03453,8.10196 0,10.24447 0,12.86337 c 0,0.01333 0,0.02659 0,0.03921 v 0.90035 c 0,3.04416 2.2329,5.51146 4.98643,5.51146 h 21.08216 c 2.75353,0 4.98701,-2.4673 4.98701,-5.51146 v -0.90035 h -0.0044 z' id='path4' clip-path='url(%23clipPath986-2)' style='fill:%234433aa;fill-opacity:1' transform='translate(35.183903,1.6335167)' /%3E%3C/svg%3E%0A");`,
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        height: "100%",
        minHeight: props.minHeight || 200,
    }),
}));

export const Image = ({ percentage, minHeight, style }) => {
    const classes = createImageClasses({ percentage });
    return <div className={classes.imageRoot} style={style}></div>;
};

export const AnimateOnScroll = ({ children, variant }) => {
    return <div data-aos={`fade-${variant}`}>{children}</div>;
};

const createFooterStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: "#404040",
        color: theme.palette.getContrastText("#404040"),
        padingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
}));

export const PublicFooter = ({
    children,
    copyright,
    copyrightOwner = "Jan Hrabal",
    copyrightOwnerUrl = "https://www.janhrabal.com",
    copyrightSince = 2021,
}) => {
    const classes = createFooterStyles();
    return (
        <Box className={classes.root}>
            <Container maxWidth="lg" sx={{ maxWidth }}>
                {!!copyright && <Copyright owner={copyrightOwner} ownerUrl={copyrightOwnerUrl} since={copyrightSince} />}
            </Container>
        </Box>
    );
};

export const Copyright = ({ since, owner, ownerUrl }) => {
    let ownerElement = <span>{owner}</span>;
    if (ownerUrl) {
        ownerElement = (
            <a href={ownerUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>
                {ownerElement}
            </a>
        );
    }

    const year = new Date().getFullYear();
    return (
        <Typography component="div" variant="body2">
            Copyright {ownerElement} © {`${!!since && since !== year ? since + " - " : ""}`}
            {year}
        </Typography>
    );
};
